/*
 * Monk cart sync
 *
 * The Monk app renders its own variant/quantity picker and calls /cart/add.js
 * directly, so it never goes through <product-form>. Everything that refreshes
 * and opens the drawer lives in product-form.js -> CartDrawer.renderContents(),
 * which means a Monk add leaves the drawer stale and closed.
 *
 * This listens for Monk's cart:refresh event (and, as a safety net, any cart
 * write that didn't come from the theme) and re-renders the cart sections using
 * the theme's own helpers. The drawer only opens when the shopper actually
 * clicked something, so Monk's silent free-gift adds don't pop it open.
 */
(function () {
  'use strict';

  const INTENT_MS = 3000;
  const LOADING_TIMEOUT_MS = 10000;
  const CART_WRITE_RE = /\/cart\/(add|change|update)(\.js)?/;
  const BUTTON_SELECTOR = 'button, [type="submit"], [role="button"], a';

  let lastIntentAt = 0;
  let lastIntentButton = null;
  let pending = null;

  const markIntent = (event) => {
    if (!event.isTrusted) return;
    lastIntentAt = Date.now();
    const target = event.target;
    lastIntentButton = target && target.closest ? target.closest(BUTTON_SELECTOR) : null;
  };

  document.addEventListener('pointerdown', markIntent, true);
  document.addEventListener('keydown', (event) => {
    if (event.code === 'Enter' || event.code === 'Space') markIntent(event);
  }, true);

  const userJustActed = () => Date.now() - lastIntentAt < INTENT_MS;

  // --- add-to-cart button loading state -----------------------------------

  const SPINNER_SVG =
    '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">' +
    '<circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>' +
    '</svg>';

  let loadingButton = null;
  let loadingTimer = null;
  let restorePosition = false;

  function beginLoading() {
    const button = lastIntentButton;
    if (!button || !button.isConnected || loadingButton) return;

    // Read the label colour before .monk-cart-loading blanks it, so the
    // spinner stays visible on both light and dark buttons.
    const styles = window.getComputedStyle(button);
    const labelColor = styles.color;
    restorePosition = styles.position === 'static';
    if (restorePosition) button.style.position = 'relative';

    const loader = document.createElement('span');
    loader.className = 'monk-cart-loader';
    loader.setAttribute('aria-hidden', 'true');
    loader.style.color = labelColor;
    loader.innerHTML = SPINNER_SVG;

    button.appendChild(loader);
    button.classList.add('monk-cart-loading');
    button.setAttribute('aria-busy', 'true');

    loadingButton = button;
    loadingTimer = setTimeout(endLoading, LOADING_TIMEOUT_MS);
  }

  function endLoading() {
    if (loadingTimer) {
      clearTimeout(loadingTimer);
      loadingTimer = null;
    }
    const button = loadingButton;
    loadingButton = null;
    if (!button) return;

    button.classList.remove('monk-cart-loading');
    button.removeAttribute('aria-busy');
    if (restorePosition) button.style.removeProperty('position');
    const loader = button.querySelector('.monk-cart-loader');
    if (loader) loader.remove();
  }

  const sectionsUrl = () => window.location.pathname;

  async function refreshDrawer(shouldOpen) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.getSectionsToRender !== 'function') return;

    const toRender = drawer.getSectionsToRender();
    const response = await fetch(`${sectionsUrl()}?sections=${toRender.map((section) => section.id).join(',')}`);
    if (!response.ok) return;
    const sections = await response.json();

    toRender.forEach((section) => {
      const target = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (!target || !sections[section.id]) return;
      target.innerHTML = drawer.getSectionInnerHTML(sections[section.id], section.selector);
    });

    // #CartDrawer was replaced, so the overlay node is new and needs rebinding
    const overlay = drawer.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));

    // is-empty lives on both <cart-drawer> and .drawer__inner
    drawer.classList.remove('is-empty');
    const drawerInner = drawer.querySelector('.drawer__inner');
    if (drawerInner) drawerInner.classList.remove('is-empty');

    if (shouldOpen && !drawer.classList.contains('active')) drawer.open();
  }

  async function refreshCartPage() {
    const cartItems = document.querySelector('cart-items');
    if (!cartItems || typeof cartItems.getSectionsToRender !== 'function') return;

    const toRender = cartItems.getSectionsToRender();
    const ids = [...new Set(toRender.map((section) => section.section))].join(',');
    const response = await fetch(`${sectionsUrl()}?sections=${ids}`);
    if (!response.ok) return;
    const sections = await response.json();

    toRender.forEach((section) => {
      const host = document.getElementById(section.id);
      if (!host || !sections[section.section]) return;
      const target = host.querySelector(section.selector) || host;
      target.innerHTML = cartItems.getSectionInnerHTML(sections[section.section], section.selector);
    });
  }

  // cart:refresh and the intercepted request can both fire for one add, so
  // coalesce them into a single section fetch.
  function scheduleRefresh(shouldOpen) {
    if (pending) {
      pending.open = pending.open || shouldOpen;
      return;
    }
    pending = { open: shouldOpen };

    setTimeout(async () => {
      const job = pending;
      pending = null;
      try {
        // The drawer isn't rendered on /cart (see theme.liquid)
        if (document.querySelector('cart-drawer')) await refreshDrawer(job.open);
        else await refreshCartPage();
      } catch (error) {
        console.error('[monk-cart-sync]', error);
      } finally {
        endLoading();
      }
    }, 120);
  }

  document.addEventListener('cart:refresh', () => {
    if (userJustActed()) beginLoading();
    scheduleRefresh(userJustActed());
  });

  // Safety net: catch cart writes Monk makes without dispatching cart:refresh.
  // product-form.js always appends `sections`, so that's what tells a theme
  // request (which opens the drawer on its own) apart from an app's.
  const isThemeRequest = (body) => {
    if (typeof body === 'string') return body.includes('sections=') || body.includes('"sections"');
    if (typeof FormData !== 'undefined' && body instanceof FormData) return body.has('sections');
    return false;
  };

  const nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const external = CART_WRITE_RE.test(url) && !isThemeRequest(init && init.body);
    if (external && userJustActed()) beginLoading();
    const request = nativeFetch.apply(this, arguments);
    if (external) {
      request
        .then((response) => {
          if (response.ok) scheduleRefresh(userJustActed());
          else endLoading();
        })
        .catch(endLoading);
    }
    return request;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  const nativeSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url) {
    this.__monkCartUrl = url;
    return nativeOpen.apply(this, arguments);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (CART_WRITE_RE.test(this.__monkCartUrl || '') && !isThemeRequest(body)) {
      if (userJustActed()) beginLoading();
      this.addEventListener('load', () => {
        if (this.status >= 200 && this.status < 300) scheduleRefresh(userJustActed());
        else endLoading();
      });
      this.addEventListener('error', endLoading);
      this.addEventListener('abort', endLoading);
    }
    return nativeSend.apply(this, arguments);
  };
})();
